import { useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RefreshCw, Moon, Sun, AlertCircle, Plus, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import StarBorder from '@/components/ui/star-border'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { PoliciesToolbar } from '@/components/policies/policies-toolbar'
import { PoliciesTable } from '@/components/policies/policies-table'
import { PoliciesPagination } from '@/components/policies/policies-pagination'
import { PoliciesEmpty } from '@/components/policies/policies-empty'
import { DeletePolicyDialog } from '@/components/policies/delete-policy-dialog'
import { BulkDeleteDialog } from '@/components/policies/bulk-delete-dialog'
import { PolicyImportDialog } from '@/components/policies/policy-import-dialog'
import {
  usePolicies,
  useDeletePolicy,
  useBulkDeletePolicies,
  useExportPolicies,
  sortPolicies,
  filterPoliciesBySearch,
  type SortConfig,
} from '@/hooks/use-policies'
import { useThemeStore } from '@/stores/theme-store'
import type { PolicyDraftListItem, PolicyDraftStatus } from '@/types/policy'
import { cn } from '@/lib/utils'

/**
 * Default pagination configuration
 */
const DEFAULT_PAGE_SIZE = 10

/**
 * Policies list page component
 * Main page for viewing and managing all policies
 */
export function PoliciesPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { theme, toggleTheme } = useThemeStore()

  // Read status filter from URL on initial mount (e.g. /pac/policies?status=ACTIVE)
  const initialStatusFilter: PolicyDraftStatus | '' = (() => {
    const s = searchParams.get('status')
    return s === 'DRAFT' || s === 'ACTIVE' || s === 'INACTIVE' ? s : ''
  })()

  // Query state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] =
    useState<PolicyDraftStatus | ''>(initialStatusFilter)

  // Sort state (client-side)
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'updatedAt',
    direction: 'desc',
  })

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Dialog state
  const [deleteDialogPolicy, setDeleteDialogPolicy] =
    useState<PolicyDraftListItem | null>(null)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  // Fetch policies
  const { data, isLoading, isError, error, refetch } = usePolicies({
    page,
    pageSize,
    status: statusFilter || undefined,
  })

  // Mutations
  const deletePolicy = useDeletePolicy()
  const bulkDeletePolicies = useBulkDeletePolicies()
  const exportPolicies = useExportPolicies()

  // Process policies with client-side filtering and sorting
  const processedPolicies = useMemo(() => {
    const policiesData = data?.data ?? []
    if (policiesData.length === 0) return []

    let result = [...policiesData]

    // Apply client-side search filter
    if (searchQuery) {
      result = filterPoliciesBySearch(result, searchQuery)
    }

    // Apply client-side sorting
    result = sortPolicies(result, sortConfig)

    return result
  }, [data?.data, searchQuery, sortConfig])

  // Check if any filters are applied
  const hasFilters = searchQuery !== '' || statusFilter !== ''

  // Check if any selected policy is deployed
  const hasDeployedSelected = useMemo(() => {
    return processedPolicies.some(
      (p) => selectedIds.has(p.id) && p.status === 'ACTIVE'
    )
  }, [processedPolicies, selectedIds])

  // Get selected policies for bulk delete dialog
  const selectedPolicies = useMemo(() => {
    return processedPolicies.filter((p) => selectedIds.has(p.id))
  }, [processedPolicies, selectedIds])

  // Handlers
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query)
    setPage(1) // Reset to first page on search
  }, [])

  const handleStatusFilterChange = useCallback(
    (status: PolicyDraftStatus | '') => {
      setStatusFilter(status)
      setPage(1) // Reset to first page on filter change
      setSelectedIds(new Set()) // Clear selection on filter change
    },
    []
  )

  const handleSortChange = useCallback((column: SortConfig['column']) => {
    setSortConfig((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }, [])

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allIds = new Set(processedPolicies.map((p) => p.id))
      const allSelected = processedPolicies.every((p) => prev.has(p.id))

      if (allSelected) {
        return new Set()
      } else {
        return allIds
      }
    })
  }, [processedPolicies])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize)
    setPage(1) // Reset to first page
  }, [])

  // Action handlers
  const handleView = useCallback(
    (policy: PolicyDraftListItem) => {
      navigate(`/pac/policies/${policy.id}`)
    },
    [navigate]
  )

  const handleEdit = useCallback(
    (policy: PolicyDraftListItem) => {
      navigate(`/pac/policies/${policy.id}/edit`)
    },
    [navigate]
  )

  const handleDelete = useCallback((policy: PolicyDraftListItem) => {
    setDeleteDialogPolicy(policy)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogPolicy) return

    try {
      await deletePolicy.mutateAsync(deleteDialogPolicy.id)
      toast.success(`Policy "${deleteDialogPolicy.name}" deleted successfully`)
      setDeleteDialogPolicy(null)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(deleteDialogPolicy.id)
        return next
      })
    } catch {
      toast.error('Failed to delete policy')
    }
  }, [deleteDialogPolicy, deletePolicy])

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return
    setBulkDeleteDialogOpen(true)
  }, [selectedIds.size])

  const handleConfirmBulkDelete = useCallback(async () => {
    const idsToDelete = selectedPolicies
      .filter((p) => p.status !== 'ACTIVE')
      .map((p) => p.id)

    if (idsToDelete.length === 0) {
      toast.error('No policies to delete')
      setBulkDeleteDialogOpen(false)
      return
    }

    try {
      const result = await bulkDeletePolicies.mutateAsync({ ids: idsToDelete })
      toast.success(`${result.count} policies deleted successfully`)
      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch {
      toast.error('Failed to delete policies')
    }
  }, [selectedPolicies, bulkDeletePolicies])

  const handleExportSelected = useCallback(() => {
    exportPolicies.mutate({ policyIds: Array.from(selectedIds) })
  }, [exportPolicies, selectedIds])

  const handleExportAll = useCallback(() => {
    const allIds = processedPolicies.map((p) => p.id)
    if (allIds.length === 0) {
      toast.error('No policies to export')
      return
    }
    exportPolicies.mutate({ policyIds: allIds })
  }, [exportPolicies, processedPolicies])

  const handleOpenImport = useCallback(() => {
    setImportDialogOpen(true)
  }, [])

  // Calculate pagination info
  const pagination = data?.pagination ?? {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  }

  const showEmptyState =
    !isLoading && processedPolicies.length === 0 && !isError

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Policies</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => navigate('/pac/policies/new')}
            className="h-8"
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            New Policy
          </Button>
          <StarBorder as="div" color="cyan" speed="4s" thickness={2}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/polix-ai')}
              className="h-8"
            >
              <Sparkles className="mr-2 h-3.5 w-3.5" />
              Create with AI
            </Button>
          </StarBorder>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="h-8"
          >
            <RefreshCw
              className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Page Description */}
        <div>
          <p className="text-sm text-muted-foreground">
            Manage your access control policies
          </p>
        </div>

        {/* Error State */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load policies:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="ml-4"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Toolbar */}
        <PoliciesToolbar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          selectedCount={selectedIds.size}
          hasDeployedSelected={hasDeployedSelected}
          onBulkDelete={handleBulkDelete}
          onClearSelection={handleClearSelection}
          onImport={handleOpenImport}
          selectedIds={selectedIds}
          onExportSelected={handleExportSelected}
          onExportAll={handleExportAll}
          isExporting={exportPolicies.isPending}
        />

        {/* Table or Empty State */}
        {showEmptyState ? (
          <PoliciesEmpty
            title={hasFilters ? 'No policies found' : 'No policies yet'}
            description={
              hasFilters
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first access control policy using poli-x AI.'
            }
            actionLabel={
              hasFilters ? 'Clear filters' : 'Create your first policy'
            }
            actionHref={hasFilters ? undefined : '/polix-ai'}
          />
        ) : (
          <>
            <PoliciesTable
              policies={processedPolicies}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onSelectAll={handleSelectAll}
              isLoading={isLoading}
              sortConfig={sortConfig}
              onSortChange={handleSortChange}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />

            {/* Pagination */}
            {!isLoading && processedPolicies.length > 0 && (
              <PoliciesPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                pageSize={pagination.pageSize}
                totalItems={pagination.totalItems}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </>
        )}
      </div>

      {/* Delete Single Policy Dialog */}
      <DeletePolicyDialog
        open={!!deleteDialogPolicy}
        onOpenChange={(open) => !open && setDeleteDialogPolicy(null)}
        policyName={deleteDialogPolicy?.name || ''}
        onConfirm={handleConfirmDelete}
        isLoading={deletePolicy.isPending}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        policies={selectedPolicies}
        onConfirm={handleConfirmBulkDelete}
        isLoading={bulkDeletePolicies.isPending}
      />

      {/* Import Dialog */}
      <PolicyImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />
    </>
  )
}

export default PoliciesPage
